import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { useMediaQuery } from "@react-hookz/web"
import clsx from "clsx"
import { motion, useViewportScroll } from "framer-motion"
import Link from "next/link"
import React from "react"
import { useEffect, useRef, useState } from "react"

import { Authentication } from "@/lib/authentication"
import clsxm from "@/lib/clsxm"
import { Geolocation } from "@/lib/geolocation"
import { Routing } from "@/lib/routing"

import { BookingSearch } from "@/components/Booking/BookingSearch"
import { Divider } from "@/components/common/Divider/Divider"
import Icon from "@/components/common/Icon/Icon"
import UnstyledLink from "@/components/common/Links/UnstyledLink"
import NextImage from "@/components/common/NextImage/NextImage"
import { NoticeList } from "@/components/Notice/List"

import { MenuIcon } from "@/constants/icons"

const variants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0, display: `none` },
}

type Props = {
  isDropdownOpen: boolean
  lastScrollDirection?: string
  onSearchChange: (search: string) => void
  onSignOut: () => void
  onTimeframeChange: (timeframe: string) => void
  scrolled: boolean
  search?: string
  showMobileNav: boolean
  showSearch: boolean
  timeframe?: string
  toggleDropdown: () => void
  patient: Authentication.Patient | void
}

class HeaderComponent extends React.PureComponent<Props> {
  public render = (): React.ReactElement => {
    return (
      <motion.header
        style={{
          opacity: this.props.showMobileNav ? 1 : 0,
          visibility: this.props.showMobileNav ? `visible` : `hidden`,
        }}
        className={clsxm(
          `fixed left-0 top-0 z-30 flex min-h-[68px] w-full items-center transition-all`,
          [
            this.props.scrolled && [`bg-white shadow-sm`],
            !this.props.scrolled && [`bg-transparent shadow-transparent`],
          ]
        )}
      >
        <nav className={`flex-1 py-2.5`}>
          <div
            className={`mx-auto flex max-w-7xl flex-wrap items-center justify-between px-4 md:flex-nowrap md:space-x-4 xl:space-x-14`}
          >
            <div
              className={clsx(
                `flex-shrink-0 flex-grow `,
                `md:order-1 md:basis-[0px]`,
                `lg:basis-auto`
              )}
            >
              <Link passHref href={`/`} className={`flex items-center`}>
                <a>
                  <NextImage
                    priority
                    src={`/svg/wellnow-logo.svg`}
                    alt={`Navigate to homepage.`}
                    width={68}
                    height={43}
                  />
                </a>
              </Link>
            </div>

            <motion.div
              key={`booking-search`}
              variants={variants}
              animate={this.props.showSearch ? `visible` : `hidden`}
              className={clsx(
                `order-3 mt-3 w-full`,
                `md:order-2 md:mt-0 md:flex-shrink`
              )}
            >
              <BookingSearch
                timeframeValue={this.props.timeframe}
                searchValue={this.props.search}
                onTimeframeChange={this.props.onTimeframeChange}
                onSearchChange={this.props.onSearchChange}
                htmlForSearch={`nav-address-search`}
                htmlForSpecialty={`nav-booking-specialty`}
                htmlForTimeframe={`nav-booking-timeframe`}
              />
            </motion.div>

            <div
              className={clsx(
                `flex flex-shrink-0 flex-grow items-center justify-end`,
                `md:order-3`,
                `lg:basis-auto`
              )}
            >
              <NavigationMenu
                onSignOut={this.props.onSignOut}
                patient={this.props.patient}
              />
            </div>
          </div>

          <NoticeList className={`mx-auto max-w-7xl p-2`} />
        </nav>
      </motion.header>
    )
  }
}

function NavigationMenu({
  patient,
  onSignOut,
}: {
  patient: Authentication.Patient | void
  onSignOut: () => void
}) {
  return (
    <DropdownMenu.Root defaultOpen={false}>
      <DropdownMenu.Trigger
        className={`flex items-center space-x-2 rounded-full border border-gray-200 bg-white py-[11px] px-4 focus:outline-none focus-visible:ring focus-visible:ring-primary-500`}
      >
        <Icon size={`xs-16`} icon={MenuIcon} />
        <span>Menu</span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        side={`bottom`}
        align={`end`}
        className={`w-56 rounded-lg bg-white py-2 tracking-tight shadow-md transition`}
      >
        {patient ? (
          <UnstyledLink
            className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
            href={`/profile`}
          >
            <DropdownMenu.Item
              className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
            >
              <span
                className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
              >
                Profile
              </span>
            </DropdownMenu.Item>
          </UnstyledLink>
        ) : (
          <>
            <UnstyledLink
              className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
              href={`/auth/sign-in`}
            >
              <DropdownMenu.Item
                className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
              >
                <span
                  className={`flex px-4 py-2 transition hover:cursor-pointer`}
                >
                  Sign in
                </span>
              </DropdownMenu.Item>
            </UnstyledLink>
            <UnstyledLink
              className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
              href={`/auth/sign-up`}
            >
              <DropdownMenu.Item
                className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
              >
                <span
                  className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
                >
                  Sign up
                </span>
              </DropdownMenu.Item>
            </UnstyledLink>
          </>
        )}

        <Divider className={`my-2`} orientation={`horizontal`} thickness={1} />
        <UnstyledLink
          className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
          href={`/about-us`}
        >
          <DropdownMenu.Item
            className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
          >
            <span
              className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
            >
              About us
            </span>
          </DropdownMenu.Item>
        </UnstyledLink>
        <UnstyledLink
          className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
          href={`/services`}
        >
          <DropdownMenu.Item
            className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
          >
            <span
              className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
            >
              Services
            </span>
          </DropdownMenu.Item>
        </UnstyledLink>
        <UnstyledLink
          className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
          href={`/faq`}
        >
          <DropdownMenu.Item
            className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
          >
            <span
              className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
            >
              FAQ
            </span>
          </DropdownMenu.Item>
        </UnstyledLink>
        <UnstyledLink
          className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
          href={`/list-your-practice`}
        >
          <DropdownMenu.Item
            className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
          >
            <span
              className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
            >
              List your practice
            </span>
          </DropdownMenu.Item>
        </UnstyledLink>
        <UnstyledLink
          className={`block ring-primary-500 focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring focus-visible:ring-transparent`}
          href={`/contact-us`}
        >
          <DropdownMenu.Item
            className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
          >
            <span
              className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
            >
              Contact us
            </span>
          </DropdownMenu.Item>
        </UnstyledLink>
        {patient ? (
          <>
            <Divider
              className={`my-2`}
              orientation={`horizontal`}
              thickness={1}
            />
            <DropdownMenu.Item
              className={`hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none`}
              onClick={onSignOut}
            >
              <span
                className={`flex px-4 py-2 transition hover:cursor-pointer hover:bg-gray-50`}
              >
                Sign out
              </span>
            </DropdownMenu.Item>
          </>
        ) : null}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export const Header = () => {
  const { patient, signOut } = React.useContext(Authentication.Context)
  const geolocation = React.useContext(Geolocation.Context)
  const router = Routing.useRouter()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [timeframe, setTimeframe] = useState(
    router.query.timeframe?.toString() || `next 7 days`
  )
  const [search, setSearch] = useState(
    geolocation.formattedAddress || router.query.search?.toString() || ``
  )

  const [scrolled, setScrolled] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showMobileNav, setShowMobileNav] = useState(true)

  const { scrollY } = useViewportScroll()
  const isSmallDevice = useMediaQuery(`only screen and (max-width : 639px)`)
  const lastScrollDirection = useRef(``)

  const onSignOut = async (): Promise<void> => {
    await signOut()
    router.push(`/`)
    toggleDropdown()
  }

  const toggleDropdown = () => {
    return setIsDropdownOpen(!isDropdownOpen)
  }

  /**
   * @returns show or hide booking-search element in navigation
   */
  useEffect(() => {
    // Using ID, could be improved by forwarding the ref instead, but does the job.
    const searchElement = document.getElementById(`home-booking-search`)

    function updateBackgroundVisibility() {
      if (scrollY.get() <= 20) {
        setScrolled(false)
      } else {
        setScrolled(true)
      }
    }

    function handleMobileNavigation() {
      if (isSmallDevice) {
        const isScrollingDown =
          scrollY.get() > 0 && scrollY.getPrevious() - scrollY.get() < 0

        if (isScrollingDown) {
          setShowMobileNav(false)
        } else {
          setShowMobileNav(true)
        }
      } else {
        setShowMobileNav(true)
      }
    }

    function handleShowSearch() {
      if (searchElement !== null) {
        /** Calculate distance for when the scroll should be triggered */
        const elementOffset = searchElement.offsetTop
        const elementHeight = searchElement.offsetHeight
        const scrollTop = scrollY.get()
        const distanceFromTop = isSmallDevice
          ? elementOffset - scrollTop - 16 - elementHeight
          : elementOffset - scrollTop - 16
        distanceFromTop > 0 ? setShowSearch(false) : setShowSearch(true)
      } else if (!showSearch) {
        return setShowSearch(true)
      }
    }
    handleShowSearch()
    updateBackgroundVisibility()
    handleMobileNavigation()

    // Following framer motion pattern for onChange events to unsubscribe
    const unsubscribeY = scrollY.onChange(handleShowSearch)
    const checkBackground = scrollY.onChange(updateBackgroundVisibility)
    const checkMobile = scrollY.onChange(handleMobileNavigation)

    return () => {
      unsubscribeY()
      checkBackground()
      checkMobile()
      // TODO: Re-write this to not require the destroy function
      scrollY.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, isSmallDevice])

  return (
    <HeaderComponent
      isDropdownOpen={isDropdownOpen}
      lastScrollDirection={lastScrollDirection.current}
      onSearchChange={setSearch}
      onSignOut={onSignOut}
      onTimeframeChange={setTimeframe}
      scrolled={scrolled}
      search={search}
      showMobileNav={showMobileNav}
      showSearch={showSearch}
      timeframe={timeframe}
      toggleDropdown={toggleDropdown}
      patient={patient}
    />
  )
}
